import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Lenis from "lenis";

export default function SmoothScroll({ children }) {
  const lenisRef = useRef(null);
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Let us control scroll position on navigation instead of the browser
    // restoring the previous position.
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;
    window.__lenis = lenis;

    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
      window.__lenis = null;
    };
  }, []);

  // On navigation: if the URL has a hash, scroll to that section (accounting for
  // the fixed navbar); otherwise jump to the top of the new page.
  useEffect(() => {
    const lenis = lenisRef.current;

    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      let attempts = 0;
      const goToSection = () => {
        const el = document.getElementById(id);
        if (el) {
          const offset = window.innerWidth >= 768 ? 104 : 84;
          if (lenis) {
            // Refresh Lenis's cached dimensions — after a route change to a
            // taller page its scroll limit is still the previous page's, which
            // would clamp the target short.
            lenis.resize();
            lenis.scrollTo(el, { offset: -offset, duration: 1.0 });
            // Re-pin once lazy images / layout settle (doc height can change).
            setTimeout(() => {
              const cur = el.getBoundingClientRect().top;
              if (Math.abs(cur - offset) > 6) {
                lenis.resize();
                lenis.scrollTo(el, { offset: -offset, duration: 0.5 });
              }
            }, 700);
          } else {
            const y = el.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top: y, behavior: "smooth" });
          }
        } else if (attempts < 25) {
          // The target page may still be rendering after a route change.
          attempts += 1;
          setTimeout(goToSection, 50);
        }
      };
      // Wait a tick so the destination route has mounted.
      setTimeout(goToSection, 60);
      return;
    }

    if (lenis) {
      lenis.scrollTo(0, { immediate: true, force: true });
      // Re-assert next frame in case an in-flight momentum/animation overrides it.
      requestAnimationFrame(() => lenis.scrollTo(0, { immediate: true, force: true }));
    }
    // Fallback for any environment where Lenis isn't driving the scroll
    // (also covers iOS Safari / Chrome momentum scrolling edge cases).
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return children;
}
